/**
 * The window/document a subtree is currently rendered into (#761).
 *
 * Preferred resolution is always from the DOM itself (see
 * {@link ownerWindowOf} in `owner_window.ts`): it needs no plumbing and is
 * automatically correct. This context covers the cases where no node or event is
 * available yet — chiefly `createPortal` targets, which must be known at render
 * time before any ref is populated.
 *
 * The default is the main window, so a component that reads it while docked
 * behaves exactly as it did before this change. `PopoutFrame` provides the
 * detached window from INSIDE its portal, so only the detached subtree sees it.
 */

import { createContext } from 'preact';
import { useContext } from 'preact/hooks';

export interface PopoutTarget {
    win: Window;
    doc: Document;
}

export const PopoutDocumentContext = createContext<PopoutTarget | null>(null);

/** The window this subtree is rendered into. */
export function useOwnerWindow(): Window {
    return useContext(PopoutDocumentContext)?.win ?? window;
}

/** The document this subtree is rendered into. */
export function useOwnerDocument(): Document {
    return useContext(PopoutDocumentContext)?.doc ?? document;
}

/**
 * The element floating layers (popovers, menus, dialogs) should portal into, in
 * the document this subtree is rendered into.
 */
export function usePortalRoot(): HTMLElement {
    return useOwnerDocument().body;
}
